/**
 * Which app this page load serves. pricing.secondarymkt.com (and pricing.dev…, pricing.localhost)
 * is the broker-only pricing site; every other host is the platform. CloudFront hides the viewer
 * Host from nginx, so the SPA decides from window.location.hostname.
 *
 * Development override (dev server only): `VITE_APP_SURFACE=pricing`, or `?surface=pricing` /
 * `?surface=platform`, remembered for the tab in sessionStorage.
 */
export type AppSurface = 'platform' | 'pricing'

export const SURFACE_STORAGE_KEY = 'smkt.surface'

const isSurface = (v: unknown): v is AppSurface => v === 'platform' || v === 'pricing'

export function isPricingHost(hostname: string): boolean {
  return /^pricing\./i.test(hostname)
}

export interface SurfaceInput {
  hostname: string
  search: string
  /** `import.meta.env.VITE_APP_SURFACE`. */
  envSurface?: string
  /** The tab's remembered override (sessionStorage). */
  stored?: string | null
  /** Overrides apply on the dev server only; production hosts decide by hostname alone. */
  allowOverride: boolean
}

/** The surface plus the override to remember (`null` = leave storage alone). */
export function pickSurface(input: SurfaceInput): { surface: AppSurface; remember: AppSurface | null } {
  if (isPricingHost(input.hostname)) return { surface: 'pricing', remember: null }
  if (!input.allowOverride) return { surface: 'platform', remember: null }
  const fromQuery = new URLSearchParams(input.search).get('surface')
  if (isSurface(fromQuery)) return { surface: fromQuery, remember: fromQuery }
  if (isSurface(input.stored)) return { surface: input.stored, remember: null }
  if (isSurface(input.envSurface)) return { surface: input.envSurface, remember: null }
  return { surface: 'platform', remember: null }
}

let current: AppSurface | null = null

/** The surface of this page load (computed once). */
export function appSurface(): AppSurface {
  if (current) return current
  if (typeof window === 'undefined') return 'platform'
  let stored: string | null = null
  try {
    stored = window.sessionStorage.getItem(SURFACE_STORAGE_KEY)
  } catch {
    /* storage unavailable */
  }
  const { surface, remember } = pickSurface({
    hostname: window.location.hostname,
    search: window.location.search,
    envSurface: import.meta.env.VITE_APP_SURFACE,
    stored,
    allowOverride: Boolean(import.meta.env.DEV),
  })
  if (remember) {
    try {
      window.sessionStorage.setItem(SURFACE_STORAGE_KEY, remember)
    } catch {
      /* storage unavailable */
    }
  }
  current = surface
  return surface
}

/**
 * The platform app's origin for a pricing host (a link for people who also use the platform):
 * pricing.secondarymkt.com → platform.secondarymkt.com, pricing.dev.secondarymkt.com →
 * dev.secondarymkt.com, pricing.localhost:5430 → localhost:5430. Null on other hosts.
 */
export function platformOrigin(location: { protocol: string; hostname: string; port: string }): string | null {
  if (!isPricingHost(location.hostname)) return null
  const rest = location.hostname.replace(/^pricing\./i, '')
  const host = rest === 'secondarymkt.com' ? 'platform.secondarymkt.com' : rest
  return `${location.protocol}//${host}${location.port ? `:${location.port}` : ''}`
}
